import { GlobalState } from "App";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const loginStates = {
  INFO: "INFO",
  LOGIN: "LOGIN",
};

export const formModes = {
  START: "START",
  ASK_QUESTION: "ASK_QUESTION",
  CREATE_REFERRAL: "CREATE_REFERRAL",
  QUESTION_SENT: "QUESTION_SENT",
  REFERRAL_SENT: "REFERRAL_SENT",
};

export const useEmbeddedForm = () => {
  const { formCode, defaultMode } = useParams();
  const { setAuthBackUrl } = useContext(GlobalState);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [originator, setOriginator] = useState(null);
  const [providers, setProviders] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loginState, setLoginState] = useState(loginStates.INFO);
  const [codeError, setCodeError] = useState("");

  const [mode, setMode] = useState(defaultMode ?? formModes.START);

  const reloadAuthData = async () => {
    const accessToken = window.localStorage.getItem(`accessToken`);

    if (!accessToken) {
      setLoading(false);
      return;
    }

    try {
      const buttonTokens = JSON.parse(
        window.localStorage.getItem(`buttonTokens`)
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check_access_token`,
        {
          method: "POST",
          body: JSON.stringify({ accessToken, buttonTokens }),
        }
      );
      const result = await response.json();

      if (result?.error || result?.message) {
        window.localStorage.removeItem(`accessToken`);
        return;
      }

      if (!result?.isTokenValid) {
        throw new Error();
      }

      setOriginator(result?.originator ?? null);
      setProviders(result?.providers ?? []);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
    }
  };

  const checkFormCode = async (code) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_V2_URL}/organizations/recipient/${code}`,
        {
          method: "GET",
        }
      );
      const result = await response.json();

      if (result?.error || result?.message) {
        setCodeError(result?.message || result?.error);
        setError(result?.message || result?.error);
        return;
      }
      const locations = [...(result?.locations ?? [])];
      setLocations(locations);
      delete result.locations;
      setRecipient(result);
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Call support");
    } finally {
      setLoading(false);
    }
  };

  const hanldeLogout = () => {
    window.localStorage.removeItem(`accessToken`);
    setOriginator(null);
    setMode(formModes.START);
    setLoginState(loginStates.INFO);
    setProviders([]);
  };

  useEffect(() => {
    reloadAuthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formCode) {
      checkFormCode(formCode);
      setAuthBackUrl(`/embedded_form/${formCode}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCode]);

  useEffect(() => {
    if (defaultMode) {
      navigate(`/embedded_form/${formCode}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMode, formCode]);

  const isIframeEmbedded = window.self !== window.top;

  return {
    formCode,
    loading,
    setLoading,
    error,
    setError,
    originator,
    reloadAuthData,
    hanldeLogout,
    providers,
    setProviders,
    recipient,
    locations,
    mode,
    setMode,
    isIframeEmbedded,
    loginState,
    setLoginState,
    codeError,
  };
};
